import { Panel } from "../../../components/panels/Panel";
import { DateRangePickerComponent } from "../../../components/others/DateRangePickerComponent";
import { ActionButton } from "../../../components/buttons/ActionButton";
import { ChevronRightIcon, ClockIcon, InboxIcon } from "@heroicons/react/20/solid";
import React, { useState, useRef, useMemo } from "react";
import {
  formatSecondsToFullDateTime,
  formatSecondsToMinutesSeconds
} from "../../../helpers/date/date-helper";
import { getInfluxdbData } from "../../../services/api/influxdb-tools";
import Badge from "../../../components/badges/Badge";
import {
  convertLogsToHistory,
  convertToSchemeHistory,
  findPoolByRealId,
  findPoolBySchemeId,
  getSchemeId
} from "../../../services/live-interface/dior/dior-interface-service";
import ToggleSwitch from "../../../components/toggle/ToggleSwitch";
import { checkProcessDivergences } from "../../../services/live-interface/dior/dior-check-errors";
import { useGetData } from "../../../services/api/api-tools";

const getStepErrorBadgeType = (error) => {
  if (['unexpected_step', 'missing_step'].includes(error.reason)) return 'red';
  if (['duration_too_short', 'duration_too_long'].includes(error.reason)) return 'yellow';
  return 'gray';
};

const getStepErrorLabel = (error) => {
  if (error.reason === 'duration_too_long') return 'Too long';
  if (error.reason === 'duration_too_short') return 'Too short';
  if (error.reason === 'missing_step') return 'Missing step';
  if (error.reason === 'unexpected_step') return 'Unexpected step';

  return 'Process error';
};

const addScanStepToProcess = (process) => [{ pool_id: -1, duration_min: 0, duration_max: Infinity }, ...process];

const createRackDisplayItems = (selectedRackHistory, processError, process) => {
  if (!selectedRackHistory) return [];

  const errors = [...(processError?.steps_error ?? [])];
  const missingDisplayIndexes = {};

  const realItems = selectedRackHistory.map((step, index) => ({
    type: 'real_step',
    step,
    index,
    displayIndex: index,
    error: null,
  }));

  const history = convertToSchemeHistory(convertLogsToHistory(selectedRackHistory));

  let theoreticalIndex = 0;

  history.forEach((historyStep, historyIndex) => {
    const theoreticalStep = process[theoreticalIndex];

    const matchingErrorIndex = errors.findIndex((error) => {
      if (error.reason === 'missing_step') return false;

      if (['duration_too_short', 'duration_too_long'].includes(error.reason)) {
        return error.id === theoreticalIndex && error.details.pool_id === historyStep.pool_id;
      }

      if (error.reason === 'unexpected_step') {
        return error.id === theoreticalIndex && error.details.pool_id === historyStep.pool_id;
      }

      return false;
    });

    if (matchingErrorIndex !== -1) {
      realItems[historyIndex].error = errors[matchingErrorIndex];
      errors.splice(matchingErrorIndex, 1);
    }

    if (theoreticalStep?.pool_id === historyStep.pool_id) {
      theoreticalIndex += 1;
      return;
    }

    const nextTheoreticalIndex = process.findIndex((step, index) => {
      return index > theoreticalIndex && step.pool_id === historyStep.pool_id;
    });

    if (nextTheoreticalIndex !== -1) {
      for (let missingIndex = theoreticalIndex; missingIndex < nextTheoreticalIndex; missingIndex++) {
        missingDisplayIndexes[missingIndex] = historyIndex - 0.5 + (missingIndex - theoreticalIndex) / 1000;
      }

      theoreticalIndex = nextTheoreticalIndex + 1;
    }
  });

  const missingItems = errors
    .filter((error) => error.reason === 'missing_step')
    .map((error) => ({
      type: 'missing_step',
      theoreticalIndex: error.id,
      expectedPoolId: error.details.expected_pool_id,
      beforePoolId: error.details.pool_id,
      displayIndex: missingDisplayIndexes[error.id] ?? realItems.length,
      error,
    }));

  return [...realItems, ...missingItems].sort((a, b) => a.displayIndex - b.displayIndex);
};

export const DiorHistoryDashboard = () => {

  /* Dates management */
  const dateRangePickerRef = useRef(null);
  const now = new Date();
  const defaultStart = new Date(now);
  defaultStart.setHours(6, 0, 0, 0);
  const defaultEnd = new Date(now);
  defaultEnd.setHours(18, 0, 0, 0);
  const yesterdayStart = new Date(now);
  const yesterdayStop = new Date(now);
  yesterdayStart.setDate(yesterdayStart.getDate() - 1);
  yesterdayStart.setHours(0, 0, 0, 0);
  yesterdayStop.setDate(yesterdayStop.getDate() - 1);
  yesterdayStop.setHours(23, 59, 59, 999);
  const thisWeekStart = new Date(now);
  const thisWeekStop = new Date(now);
  const currentDay = thisWeekStart.getDay();
  const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;
  thisWeekStart.setDate(thisWeekStart.getDate() + mondayOffset);
  thisWeekStart.setHours(0, 0, 0, 0);
  thisWeekStop.setDate(thisWeekStart.getDate() + 6);
  thisWeekStop.setHours(23, 59, 59, 999);

  const [selectedRange, setSelectedRange] = useState({ start: defaultStart, end: defaultEnd });

  const handleRangeSelection = (start, end) => {
    setSelectedRange({ start, end });
    setRacksList([]);
    setSelectedRackHistory(null);

    updateData(Math.floor(start / 1000), Math.floor(end / 1000));
  };

  /* Racks list management */
  const [racksData, setRacksData] = useState([]);
  const [racksList, setRacksList] = useState([]);
  const [selectedRackHistory, setSelectedRackHistory] = useState(null);
  const [showAguasPools, setShowAguasPools] = useState(false);

  const updateData = async (start, end) => {
    const newRacksData = await getInfluxdbData(
      'engine',
      'dives',
      [],
      ['in_timestamp_ms', 'duration'],
      start,
      end,
      '1ms',
      null,
      null,
      [],
      null,
      null,
      false,
      'asc',
    );

    setRacksData(newRacksData);
    setRacksList(createRacksList(newRacksData));
  };

  const createRacksList = (rawData) => {
    const seen = new Set();

    return [...rawData]
      .sort((a, b) => a.in_timestamp_ms - b.in_timestamp_ms)
      .filter(item => {
        if (seen.has(item.rack_id)) return false;
        seen.add(item.rack_id);
        return true;
      })
      .map(item => ({
        rack_id: item.rack_id,
        type: item.type,
        time_s: Math.floor(item.in_timestamp_ms / 1000),
      }));
  };

  const handleRackSelection = (rackType, rackId) => {
    setSelectedRackHistory(createRackItemsList(rackType, rackId));
  };

  const createRackItemsList = (rackType, rackId) => {
    let rackItemsList = [...racksData]
      .filter(item => item.type === rackType && item.rack_id === rackId)
      .sort((a, b) => a.in_timestamp_ms - b.in_timestamp_ms)
      .map(item => ({
        ...item,
        time_s: Math.floor(item.in_timestamp_ms / 1000),
      }));

    return mergeSuccessiveLogsBySchemeIdAndRackId(rackItemsList);
  };

  const mergeSuccessiveLogsBySchemeIdAndRackId = (logs) => {
    if (!Array.isArray(logs)) return [];

    return logs.reduce((mergedLogs, log) => {
      const previousLog = mergedLogs[mergedLogs.length - 1];

      if (!previousLog) {
        mergedLogs.push({ ...log });
        return mergedLogs;
      }

      const previousSchemeId = getSchemeId(previousLog.pool_id);
      const currentSchemeId = getSchemeId(log.pool_id);

      const shouldMerge =
        previousSchemeId === currentSchemeId &&
        previousLog.rack_id === log.rack_id;

      if (!shouldMerge) {
        mergedLogs.push({ ...log });
        return mergedLogs;
      }

      previousLog.duration =
        Number(previousLog.duration ?? 0) + Number(log.duration ?? 0);

      return mergedLogs;
    }, []);
  };

  /* Process errors check */
  const { data: processes, isLoading: processesAreLoading } = useGetData('patterns', `patterns`, {}, !!localStorage.getItem('token'));
  let process = useMemo(() => {
    if (processesAreLoading || selectedRackHistory === null) return [];
    const process = processes.find((process) => process.name === selectedRackHistory?.[0]?.type)?.pattern_data?.[0]?.json;
    return process ?? [];
  }, [processes, selectedRackHistory]);


  const processWithScanStep = addScanStepToProcess(process);
  const selectedRackSchemeHistory = selectedRackHistory ? convertToSchemeHistory(convertLogsToHistory(selectedRackHistory)) : null;
  const processError = selectedRackSchemeHistory ? checkProcessDivergences(processWithScanStep, selectedRackSchemeHistory, true) : null;
  const rackDisplayItems = createRackDisplayItems(selectedRackHistory, processError, processWithScanStep);

  return (
    <Panel category='main' title='DASHBOARD'>
      <div className="h-full flex flex-col gap-4">
        <Panel noHeader category="secondary" rounded height="h-16">
          <div className="flex gap-8 items-center h-full">
            <div className="flex gap-4 items-center">
              <ActionButton text="Today" size="sm" onClick={() => dateRangePickerRef.current?.selectAndValidate(defaultStart, defaultEnd)}/>
              <ActionButton text="Yesterday" size="sm" onClick={() => dateRangePickerRef.current?.selectAndValidate(yesterdayStart, yesterdayStop)}/>
              <ActionButton text="This week" size="sm" onClick={() => dateRangePickerRef.current?.selectAndValidate(thisWeekStart, thisWeekStop)}/>

              <DateRangePickerComponent
                ref={dateRangePickerRef}
                trigger={({ isCalendarOpen, setIsCalendarOpen }) => (
                  <ActionButton text="Select date range" color="secondary" Icon={ClockIcon} size="sm" onClick={() => setIsCalendarOpen(!isCalendarOpen)} />
                )}
                defaultStart={defaultStart} defaultEnd={defaultEnd}
                callback={handleRangeSelection}
                cancelText="Cancel" validateText="Load racks"
                alwaysShowValidateButton={true}
                storageKey="dashboard-date-range"
              />
            </div>

            <div className="flex gap-2 items-center">
              Show "AGUAS" pools
              <ToggleSwitch enabled={showAguasPools} setEnabled={setShowAguasPools} storageToggleId="show_aguas_pool"/>
            </div>
          </div>
        </Panel>
        <div className="h-full flex-1 flex gap-4 min-h-0">
          <Panel title="RACKS LIST" rounded>
            <div className="flex flex-col gap-4 h-full">
              {
                racksList.map((rack, rackIndex) => (
                  <div className={`flex bg-perception-black-700 rounded-md p-4 gap-4 items-center relative group cursor-pointer
                    border border-2 ${selectedRackHistory?.[0]?.rack_id === rack.rack_id ? 'border-perception-blue' : 'border-transparent'}`}
                       onClick={() => handleRackSelection(rack.type, rack.rack_id)} key={rackIndex}
                  >
                    {rack.type === 'GA000'
                      ? <Badge text="RACK NOT SCANNED" type="red"/>
                      : rack.type === 'GA999'
                        ? <Badge text="REWORK" type="yellow"/>
                        : rack.type === 'GA001'
                          ? <Badge text="CATAFORESE" type="blue"/>
                          : <Badge text={rack.type} type="blue"/>
                    }
                    <div>{formatSecondsToFullDateTime(rack.time_s, false)}</div>
                    <div className="text-perception-gray-600 text-sm">{rack.rack_id > 10000 ? '(No ID)' : `#${rack.rack_id}`}</div>

                    <div className="absolute right-4 group-hover:right-2 transition-all top-1/2 -translate-y-1/2">
                      <ChevronRightIcon className="size-8 text-perception-gray-600"/>
                    </div>
                  </div>
                ))
              }
              {
                racksList.length === 0 &&
                <div className="flex flex-col gap-4 h-full items-center justify-center text-center">
                  <InboxIcon className="size-32 text-perception-gray-700"/>
                  <div className="text-perception-gray-700 text-2xl">Please select a date range</div>
                </div>
              }
            </div>
          </Panel>
          <Panel title="RACK DETAILS" rounded>
            {
              selectedRackHistory ?
                <div className="flex flex-col gap-2">
                  <div className="flex gap-4 items-center">
                    {selectedRackHistory[0].type === 'GA000'
                      ? <Badge text="RACK NOT SCANNED" type="red"/>
                      : selectedRackHistory[0].type === 'GA999'
                        ? <Badge text="REWORK" type="yellow"/>
                        : selectedRackHistory[0].type === 'GA001'
                          ? <Badge text="CATAFORESE" type="blue"/>
                          : <Badge text={selectedRackHistory[0].type} type="blue"/>
                    }
                    <div>{formatSecondsToFullDateTime(selectedRackHistory[0].time_s, false)}</div>
                    <div className="text-perception-gray-600 text-sm">{selectedRackHistory[0].rack_id > 10000 ? '(No ID)' : `#${selectedRackHistory[0].rack_id}`}</div>
                  </div>
                  {
                    rackDisplayItems
                      .filter((item) => { // Filter "AGUAS" pools
                        if (item.type !== 'missing_step' && parseInt(item.step.pool_id) === -2) return false;
                        if (showAguasPools) return true;
                        if (item.type === 'missing_step')
                          return !findPoolBySchemeId(item.expectedPoolId)?.name.includes('AGUAS');
                        return !findPoolByRealId(item.step.pool_id)?.name.includes('AGUAS');
                      })
                      .map((item, rackStepIndex) => {
                        if (item.type === 'missing_step') {
                          return (
                            <div className="flex bg-error/10 rounded-md px-4 py-2 gap-4 items-center relative" key={`missing-${rackStepIndex}`}>
                              <div className="text-xs text-perception-gray-600">#{rackStepIndex + 1}</div>
                              <div className="w-full flex justify-between">
                                <div className="flex gap-2">
                                  {item.expectedPoolId >= 0 && <Badge text={item.expectedPoolId} type="blue"/>}
                                  {findPoolBySchemeId(item.expectedPoolId)?.name}
                                </div>
                                <Badge text="Missing step" type="red"/>
                              </div>
                            </div>
                          );
                        }

                        const rackStep = item.step;

                        return (
                          <div className={`flex ${item.error?.reason === 'unexpected_step' ? 'bg-error/10' : 'bg-perception-black-700'} rounded-md gap-4 items-center py-2 px-4 relative`} key={`step-${rackStepIndex}`}>
                            <div className="text-xs text-perception-gray-600">#{rackStepIndex + 1}</div>
                            <div className="w-full flex justify-between items-center gap-4">
                              <div className="flex-1 flex gap-4 items-center">
                                <Badge text={formatSecondsToFullDateTime(rackStep.time_s, false)}/>
                                <div className="flex gap-2">
                                  {rackStep.pool_id >= 0 && <Badge text={getSchemeId(rackStep.pool_id)} type="blue"/>}
                                  {findPoolByRealId(rackStep.pool_id)?.name}
                                </div>
                                {rackStep.pool_id >= 0 &&
                                  <div className="bg-perception-black-500 px-2 py-0.5 rounded-md text-sm">{formatSecondsToMinutesSeconds(rackStep.duration)}</div>
                                }
                              </div>

                              <div className="min-w-0">
                                {item.error
                                  ? <Badge text={getStepErrorLabel(item.error)} type={getStepErrorBadgeType(item.error)}/>
                                  : <Badge text="VALID" type="green"/>
                                }
                              </div>
                            </div>
                          </div>
                        );
                      })
                  }
                </div>
                :
                <div className="flex flex-col gap-4 h-full items-center justify-center text-center">
                  <InboxIcon className="size-32 text-perception-gray-700"/>
                  <div className="text-perception-gray-700 text-2xl">No rack selected</div>
                </div>
            }
          </Panel>
        </div>
      </div>
    </Panel>
  );
};